import { Comment, InlineCommentClickHandler } from '@amzn/awscat-editor';
import type { CommentWhileEditingStorage } from '@amzn/awscat-editor';
import { InlineComment } from '@amzn/awscat-pattern-library-service-model';
import _ from 'lodash';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useInlineCommentManager } from '../../../contexts/InlineCommentManagerContext';
import { usePendingFlushCoordinator } from '../../../contexts/PendingFlushCoordinatorContext';
import { useSuggestionNavigationService } from '../../../contexts/SuggestionNavigationServiceContext';
import { docIdEquals } from '../../../model/RichtextDocId';
import { CommentPositionUpdate } from '../../../services/InlineCommentManager';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { SectionCommentEntry } from '../../../services/SuggestionNavigationService';
import { useFilteredInlineComments } from '../../v2-hooks/useFilteredInlineComments';
import { resolveCoordsAtPos } from './EditorCoords';
import { scrollIntoComfortZone } from './Window';
import { Editor } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { log } from '../../../loggers/Logger';
import { DEFAULT_INLINE_TEXT_FILTER, PopoverState } from './Constants';
import { useStableDocId } from './useStableDocId';

/** Resolves the text-content range for a node so inline decorations can attach to it. */
export function calculateCommentOffset(
  doc: ProseMirrorNode,
  nodeId: string,
): { readonly startOffset: number; readonly endOffset: number } | undefined {
  let result: { readonly startOffset: number; readonly endOffset: number } | undefined;
  doc.descendants((node, pos) => {
    if (node.attrs.nodeId === nodeId) {
      // pos is the opening token; pos + nodeSize is the closing token.
      // Inline decorations only render on text content, so exclude both boundary tokens.
      result = { startOffset: pos + 1, endOffset: pos + node.nodeSize - 1 };
      return false;
    }
    return true;
  });
  return result;
}

/** Resolves a page-absolute DOMRect at the start of the editor document (position 0). */
function computeDocStartPageRect(editor: Editor): DOMRect {
  const coords = resolveCoordsAtPos(editor.view, 0);
  return new DOMRect(
    coords.left + window.scrollX,
    coords.top + window.scrollY,
    0,
    coords.bottom - coords.top,
  );
}

/** Resolves a page-absolute DOMRect for an inline comment in the editor. */
function computeCommentPageRect(editor: Editor, comment: InlineComment): DOMRect | undefined {
  // Use decoration positions which track the live document state after edits,
  // rather than raw server-side offsets that may have shifted.
  const storage = editor.storage['commentWhileEditing'] as CommentWhileEditingStorage | undefined;
  if (!storage) return undefined;

  const positions = storage.getDecorationPositions(comment.commentId);
  const first = positions[0];
  if (!first) return undefined;

  const coords = resolveCoordsAtPos(editor.view, first.from);
  return new DOMRect(
    coords.left + window.scrollX,
    coords.top + window.scrollY,
    0,
    coords.bottom - coords.top,
  );
}

export interface InlineCommentIntegration {
  readonly editorComments: Comment[] | undefined;
  readonly stageCommentPositions: (comments: CommentPositionUpdate[]) => void;
  readonly handleInlineCommentClick: InlineCommentClickHandler;
  readonly popover: PopoverState | undefined;
  readonly hidePopover: () => void;
}

export function useInlineCommentIntegration(
  editor: Editor | undefined,
  inlineTextFilter: InlineTextFilter | undefined,
): InlineCommentIntegration {
  // Preps
  const [popover, setPopover] = useState<PopoverState | undefined>(undefined);
  const documentId = useStableDocId(inlineTextFilter);
  const navigationService = useSuggestionNavigationService();

  // Optimization: create a Popover ref so Popover state can be accessed
  // from inside useCallback, without this, when popover state changes,
  // the callback will change and causes Tiptap to re-render
  const popoverRef = useRef<PopoverState | undefined>(undefined);
  popoverRef.current = popover;

  // Retrieve inline comments for this content region
  const inlineCommentManager = useInlineCommentManager();
  const flushCoordinator = usePendingFlushCoordinator();
  const filteredComments = useFilteredInlineComments(
    inlineTextFilter ?? DEFAULT_INLINE_TEXT_FILTER,
  );
  const editorComments = useMemo(() => {
    if (filteredComments.length === 0) return undefined;
    const doc = editor?.state?.doc;
    const mapped: Comment[] = [];
    for (const c of filteredComments) {
      if (c.nodeId) {
        // AI/scanner comments are anchored by nodeId — resolve position from the live doc
        if (!doc) continue;
        const offset = calculateCommentOffset(doc, c.nodeId);
        if (!offset) {
          log.warn(`Comment ${c.commentId} references nodeId "${c.nodeId}" not found in document`);
          continue;
        }
        mapped.push({
          commentId: c.commentId,
          startOffset: offset.startOffset,
          endOffset: offset.endOffset,
          textData: c.textData,
        });
      } else {
        mapped.push({
          commentId: c.commentId,
          startOffset: c.startOffset,
          endOffset: c.endOffset,
          textData: c.textData,
        });
      }
    }

    return mapped.length > 0 ? mapped : undefined;
    // Document position shifts are tracked by Tiptap via updateInlineCommentsLocation.
  }, [filteredComments, editor]);

  // When comments are re-generated, send it to Tiptap.
  // Without this, comments will never be updated, as the "comments" property
  // on <TiptapEditor /> only sets the initial comments.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setUpdatedComments(editorComments ?? []);
  }, [editor, editorComments]);

  // When the editor reports shifted comment positions after content reflow.
  // Only non-AI comments need position staging — AI comments are anchored by nodeId.
  const throttledStage = useMemo(
    () =>
      _.throttle(
        (comments: CommentPositionUpdate[]) => {
          inlineCommentManager.stageCommentPositions(comments);
        },
        3_000,
        {
          leading: false,
          trailing: true,
        },
      ),
    [inlineCommentManager],
  );

  // Register with the coordinator so a browser-close drain flushes any pending
  // position update before the page is frozen.
  useEffect(() => {
    const unregister = flushCoordinator.registerFlusher(() => throttledStage.flush());
    return () => {
      unregister();
    };
  }, [flushCoordinator, throttledStage]);

  const aiCommentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of filteredComments) {
      if (c.nodeId) ids.add(c.commentId);
    }
    return ids;
  }, [filteredComments]);

  const stageCommentPositions = useCallback(
    (comments: CommentPositionUpdate[]) => {
      const nonAiComments = comments.filter((c) => !aiCommentIds.has(c.commentId));
      if (nonAiComments.length === 0) return;
      throttledStage(nonAiComments);
    },
    [aiCommentIds, throttledStage],
  );

  // When navigation moves to an inline comment, compute the node position and show the popover.
  useEffect(() => {
    if (!documentId) {
      return;
    }
    return navigationService.on('navigationItemChanged', ({ item }) => {
      if (item.type === 'doc_inline_comment' && docIdEquals(item.documentId, documentId)) {
        if (!editor || editor.isDestroyed) return;

        const comment = filteredComments.find((c) => c.commentId === item.commentId);
        if (!comment) return;

        const pageRect = computeCommentPageRect(editor, comment);
        const isCommentOnDeletedText = !pageRect;
        const resolvedRect = pageRect ?? computeDocStartPageRect(editor);

        setPopover({
          type: 'inline_comment',
          target: resolvedRect,
          commentId: item.commentId,
          nodeId: comment.nodeId,
          comment,
          isCommentOnDeletedText,
        });

        // Scroll to the comment, but not immediately, as it might conflict with
        // right hand side automatic scrolling
        window.setTimeout(() => {
          scrollIntoComfortZone(resolvedRect);
        }, 50);
        inlineCommentManager.selectThread(item.commentId);
      } else {
        setPopover(undefined);
      }
    });
  }, [navigationService, editor, documentId, filteredComments, inlineCommentManager]);

  // When the sidebar requests revealing a comment in the editor, navigate to it (show popover).
  useEffect(() => {
    return inlineCommentManager.on('revealInEditorRequested', (threadId) => {
      const comment = filteredComments.find((c) => c.commentId === threadId);
      if (!comment) return;

      navigationService.moveInto({ type: 'doc_inline_comment', commentId: threadId });
    });
  }, [inlineCommentManager, filteredComments, navigationService]);

  // When inline comments change, report them to the navigation service for next/prev navigation
  useEffect(() => {
    if (!documentId) return;

    const commentItems: SectionCommentEntry[] = filteredComments
      .filter((c) => !c.threadId)
      .map((c) => {
        if (c.nodeId && editor?.state?.doc) {
          const offset = calculateCommentOffset(editor.state.doc, c.nodeId);
          return { commentId: c.commentId, sortingOrder: offset?.startOffset ?? c.startOffset };
        }
        return { commentId: c.commentId, sortingOrder: c.startOffset };
      });

    navigationService.report({ type: 'doc_inline_comment', documentId, comments: commentItems });
    return () => {
      navigationService.clear({ type: 'doc_inline_comment', documentId });
    };
  }, [navigationService, documentId, filteredComments, editor]);

  // When the popover's comment no longer exists in the filtered list, dismiss the popover
  useEffect(() => {
    if (!popover || popover.type !== 'inline_comment') return;

    const commentStillExists = filteredComments.some((c) => c.commentId === popover.commentId);
    if (!commentStillExists) {
      setPopover(undefined);
    }
  }, [popover, filteredComments]);

  // When the inline comment popover is showing, recalculate its position on editor updates
  useEffect(() => {
    if (!popover || popover.type !== 'inline_comment' || !editor || editor.isDestroyed) return;

    const { commentId } = popover;
    const recalc = () => {
      const comment = filteredComments.find((c) => c.commentId === commentId);
      if (!comment) {
        setPopover(undefined);
        return;
      }

      const pageRect = computeCommentPageRect(editor, comment);
      const isCommentOnDeletedText = !pageRect;
      const resolvedRect = pageRect ?? computeDocStartPageRect(editor);

      setPopover((prev) =>
        prev ? { ...prev, target: resolvedRect, isCommentOnDeletedText } : undefined,
      );
    };

    editor.on('update', recalc);
    window.addEventListener('resize', recalc);
    return () => {
      editor.off('update', recalc);
      window.removeEventListener('resize', recalc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popover?.type === 'inline_comment' ? popover.commentId : null, editor, filteredComments]);

  // When the user clicks an inline comment highlight in the editor
  const handleInlineCommentClick: InlineCommentClickHandler = useCallback(
    (comment: Comment) => {
      inlineCommentManager.revealInSidebar(comment.commentId);
      navigationService.moveInto({ type: 'doc_inline_comment', commentId: comment.commentId });
    },
    [inlineCommentManager, navigationService],
  );

  const hidePopover = useCallback(() => {
    setPopover(undefined);
  }, []);

  return {
    editorComments,
    stageCommentPositions,
    handleInlineCommentClick,
    popover,
    hidePopover,
  };
}
